import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text } from '@reservamos/elements';
import InsuranceAncillarySwitch from '../InsuranceAncillarySwitch';

const propTypes = {
  enablesInsurance: PropTypes.bool.isRequired,
  wantsInsurance: PropTypes.bool.isRequired,
  hasIneligibleDoc: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  insuranceData: PropTypes.shape({
    message: PropTypes.string,
    price: PropTypes.string,
    singlePrice: PropTypes.bool,
    plusTaxes: PropTypes.bool,
    fullPrice: PropTypes.string,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * InsuranceSection component.
 * Render the insurance ancillary switch with eligibility validation.
 * Displays an error message when insurance is unavailable due to ineligible document types.
 *
 * @param {object} props - enablesInsurance, wantsInsurance, hasIneligibleDoc, onToggle, isLoading, insuranceData, t.
 * @param {boolean} props.enablesInsurance - Whether insurance is enabled for the purchase.
 * @param {boolean} props.wantsInsurance - Current state of insurance selection.
 * @param {boolean} [props.hasIneligibleDoc] - Whether any passenger has an ineligible document type.
 * @param {Function} props.onToggle - Handler function for toggling insurance selection.
 * @param {boolean} [props.isLoading] - Loading state for insurance updates.
 * @param {object} props.insuranceData - Insurance display data with message, price, singlePrice, plusTaxes, fullPrice.
 * @param {Function} props.t - Translation function from i18next.
 */
const InsuranceSection = ({
  enablesInsurance,
  wantsInsurance,
  hasIneligibleDoc,
  onToggle,
  isLoading,
  insuranceData,
  t,
}) => {
  if (!enablesInsurance) {
    return null;
  }

  const { message, price, singlePrice, plusTaxes, fullPrice } = insuranceData;

  return (
    <>
      <InsuranceAncillarySwitch
        checked={wantsInsurance}
        disabled={hasIneligibleDoc}
        isLoading={isLoading}
        onChange={onToggle}
        id="insurance"
        message={message}
        price={price}
        singlePrice={singlePrice}
        plusTaxes={plusTaxes}
        fullPrice={fullPrice}
      />
      {hasIneligibleDoc && (
        <Spacing vertical size="XS">
          <Text size="S" color="error">
            {t('insurance:not_available_for_selected_document')}
          </Text>
        </Spacing>
      )}
    </>
  );
};

InsuranceSection.propTypes = propTypes;

export default InsuranceSection;
